import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Star, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Stars } from "@/pages/patient/SuvidhaCentre";
import { api, errMsg, fmtDate, fmtDT, useApi } from "@/lib/api";

export const RatingBadge = ({ rating, count, testId = "doctor-rating" }) => count ? (
  <span className="inline-flex items-center gap-1 text-xs font-medium text-[#17232E]" data-testid={testId}>
    <Star className="h-3.5 w-3.5 fill-[#FF9700] text-[#FF9700]" />{rating?.toFixed(1)}<span className="muted font-normal">· {count} rating{count === 1 ? "" : "s"}</span>
  </span>
) : <span className="text-xs muted" data-testid={`${testId}-new`}>New · no ratings yet</span>;

export const ReviewItem = ({ r, extra }) => (
  <div className="py-3 border-b border-[#E2E8ED] last:border-0 text-sm" data-testid={`review-${r.id}`}>
    <div className="flex items-center justify-between gap-2"><Stars value={r.stars} size="h-3.5 w-3.5" /><span className="text-[11px] muted">{fmtDate(r.created_at)}</span></div>
    {r.comment && <p className="mt-1.5 text-[#17232E]">{r.comment}</p>}
    <p className="text-xs muted mt-1">{r.patient_first}{extra}</p>
  </div>
);

export function DoctorReviewList({ doctorId }) {
  const { data } = useApi(`/doctors/${doctorId}/reviews?limit=10`);
  if (!data?.items?.length) return null;
  return (
    <section className="mt-6" data-testid="doctor-reviews">
      <h3 className="font-semibold mb-1">Patient reviews</h3>
      {data.items.map((r) => <ReviewItem key={r.id} r={r} />)}
    </section>
  );
}

export function RateDialog({ appt, existing, open, onClose, onSaved }) {
  const [f, setF] = useState({ stars: 0, comment: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ stars: existing?.stars || 0, comment: existing?.comment || "" }); }, [open, existing]);
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/appointments/${appt.id}/review`, f); toast.success("Thank you for your feedback"); onSaved?.(data); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="rate-dialog">
        <DialogHeader><DialogTitle>Rate your consultation</DialogTitle><DialogDescription>{appt.doctor_name} · {fmtDT(appt.start_at)}</DialogDescription></DialogHeader>
        <Stars value={f.stars} onChange={(n) => setF({ ...f, stars: n })} size="h-8 w-8" />
        <Textarea maxLength={500} placeholder="Share what went well or what could be better (optional)" value={f.comment} onChange={(e) => setF({ ...f, comment: e.target.value })} data-testid="rate-comment" />
        <p className="text-[11px] muted text-right">{f.comment.length}/500 · Only your first name is shown. You can edit for 7 days.</p>
        <DialogFooter><Button className="rounded-full" disabled={!f.stars || busy} onClick={save} data-testid="rate-submit">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}{existing ? "Update rating" : "Submit rating"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function RateCard({ appt }) {
  const { data, error, reload } = useApi(`/appointments/${appt.id}/review`);
  const [open, setOpen] = useState(() => window.location.hash === "#rate");
  if (error) return null;
  return (
    <div className="surface p-6" id="rate" data-testid="rate-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="font-semibold">{data ? "Your rating" : "Rate your consultation"}</h3>
          {data ? <div className="mt-1"><Stars value={data.stars} size="h-4 w-4" />{data.comment && <p className="text-sm mt-1.5">{data.comment}</p>}</div>
            : <p className="text-sm muted mt-1">How was your consultation with {appt.doctor_name}?</p>}
        </div>
        {(!data || data.can_edit) && <Button variant={data ? "outline" : "default"} className="rounded-full" onClick={() => setOpen(true)} data-testid="rate-open-btn">{data ? "Edit rating" : "Rate now"}</Button>}
      </div>
      <RateDialog appt={appt} existing={data} open={open} onClose={() => setOpen(false)} onSaved={reload} />
    </div>
  );
}

export function PendingRatings() {
  const { data, reload } = useApi("/patient/pending-reviews");
  const [appt, setAppt] = useState(null);
  const [all, setAll] = useState(false);
  if (!data?.length) return null;
  return (
    <div className="surface p-5 mt-4 border-[#FF9700]/40 bg-[#FFF8EB]" data-testid="pending-ratings">
      <p className="font-semibold text-sm flex items-center gap-1.5"><Star className="h-4 w-4 fill-[#FF9700] text-[#FF9700]" />Rate your recent consultations</p>
      <div className="mt-2 divide-y divide-[#F5C26B]/50">
        {(all ? data : data.slice(0, 3)).map((a) => (
          <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm" data-testid={`pending-rating-${a.id}`}>
            <Link to={`/patient/appointments/${a.id}`} className="hover:underline">{a.doctor_name} · {fmtDT(a.start_at)}{a.is_follow_up ? " · Follow-up" : ""}</Link>
            <Button size="sm" className="rounded-full" onClick={() => setAppt(a)} data-testid={`pending-rate-btn-${a.id}`}>Rate</Button>
          </div>))}
      </div>
      {data.length > 3 && <button className="mt-1 text-xs font-medium text-[#2412B5]" onClick={() => setAll(!all)} data-testid="pending-ratings-toggle">{all ? "Show less" : `Show all ${data.length}`}</button>}
      {appt && <RateDialog appt={appt} open onClose={() => setAppt(null)} onSaved={reload} />}
    </div>
  );
}
